export class Time implements MerchantosTime {
  /**
   * Validates that a time input field is of a valid format. Leaves native
   * time inputs as they are, and assumes all others should be formatted per
   * timepicker specifications. Invalid values get reset to midnight.
   *
   * @param {HTMLInputElement} timeField The time input field
   */
  checkTimeField = (timeField: HTMLInputElement) => {
    // Don't perform the extra validation if time is already a valid string
    if (!this.isISOTimeString(timeField.value)) {
      const timeCheck = this.splitTimeString(timeField.value);
      if (timeCheck === '') {
        // eslint-disable-next-line no-param-reassign
        timeField.value = '';
      }
    }
  };

  /**
   * Takes an input and returns a string formatted per ISO 8601 spec; this is
   * currently necessary because timepicker uses text fields with nonstandard
   * formatting
   *
   * @param {HTMLInputElement} time An HTML input field
   * @returns {string} A properly-formatted time string
   */
  convertTimeTo24Hours = (time: HTMLInputElement) => {
    if (time.value && this.isISOTimeString(time.value)) {
      return time.value;
    }

    // time.value looks like "05:15 PM". Need to split the numbers, and the AM/PM
    const timeArray = this.splitTimeString(time.value);

    if (timeArray === '') {
      return '';
    }

    // If time is PM and less than 12,  need to add 12 to convert it to
    // 24 hour time before saving in the DB.
    if ((timeArray[2] === 'pm' || timeArray[2] === 'PM') && timeArray[0] < 12) {
      timeArray[0] = parseInt(timeArray[0], 10) + 12;
    } else if (
      (timeArray[2] === 'am' || timeArray[2] === 'AM') &&
      // eslint-disable-next-line eqeqeq
      parseInt(timeArray[0], 10) == 12
    ) {
      // eslint-disable-next-line no-param-reassign
      (time as any as number) = parseInt(timeArray[0], 10) - 12;
      timeArray[0] = (time as any as number) < 10 ? `0${time}` : `${time}`;
    } else if (
      (timeArray[2] === 'am' || timeArray[2] === 'AM') &&
      parseInt(timeArray[0], 10) > 12
    ) {
      return '';
    }

    return `${timeArray[0]}:${timeArray[1]}:00`;
  };

  /**
   * Checks that an array has valid formatting per our timepicker's
   * implementation. Ensures that all values are in valid ranges.
   *
   * @param {array} timeArray - a valid array will look like ["4", "15", "PM"]
   * @returns {boolean} Whether the array is valid per our formatting
   */
  private isValidTime = (timeArray: TSFixMe) => {
    if (timeArray.length !== 3) {
      return false;
    }

    if (timeArray[0] > 12 || timeArray[1] < 0) {
      return false;
    }

    if (timeArray[1] > 59 || timeArray[1] < 0) {
      return false;
    }

    const timeSuffix = ['am', 'AM', 'pm', 'PM'];

    if (timeSuffix.indexOf(timeArray[2]) === -1) {
      return false;
    }

    return true;
  };

  // var timeString [String] - A valid timestring will look like "4:15 PM";
  // The time picker's built in validations weed out invalid strings, splitting
  // the time string makes the necessary formatting easier.
  private splitTimeString = (timeString: TSFixMe) => {
    // If the time does not have : it's a wrong format
    if (timeString.indexOf(':') === -1) {
      return '';
    }

    let timeArray = timeString.split(':');

    // timeArray = [timeArray[0]].concat(timeArray.pop().split(" "));
    // In case there is no space with the minutes and the "am/pm", the time is still valid
    timeArray = [timeArray[0]].concat(
      timeArray
        .pop()
        .replace(/\s?([ap]m)/i, ' $1')
        .split(' ')
    );
    timeArray = this.isValidTime(timeArray) ? timeArray : '';
    return timeArray;
  };

  /**
   * Checks whether a string is a valid time string via the ISO 8601 spec
   * This doesn't include the timezone offset, which should be split off earlier
   *
   * @param timeString A string that represents a time field value without the
   * 					 timezone offset
   * @returns {boolean} True if the value matches, otherwise false
   */
  private isISOTimeString = (timeString: TSFixMe) => {
    // The hour portion of the time string; this needs to be a zero-padded
    // value between 00 and 23 (24 is handled separately as a special case)
    let regex = '^([0-1]\\d|2[0-3])';

    // The minute portion of the time string; needs to be a zero-padded
    // value between 00 and 59
    regex += '(:[0-5]\\d)';

    // Seconds are optional zero-padded values between 00 and 60 (leap second)
    regex += '(:([0-5]\\d|60)';

    // Fractional seconds are also acceptable
    regex += '(\\.\\d+)?)?$';

    // 24:00:00 is a special case; midnight can be represented as either
    // 24:00:00 or 00:00:00
    regex += '|(^24:00(:00)?$)';

    const regexObj = new RegExp(regex, 'g');
    const found = timeString.match(regexObj);

    return Boolean(found);
  };
}

window.merchantos.time = new Time();
